import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../api';
import {
  ShiftAnalysisRequest,
  SubAnalysisMultiResult,
  SubAnalysisResult,
  StrategicValue
} from '../../api/subAnalysis';

interface Props {
  annualPrice: number | undefined | null;
  contractDurationMonths?: number | undefined | null;
  directLaborCost: number | undefined | null;
  hoursPerYear: number | undefined | null;
  monthlyValue: number | undefined | null;
  /**
   * Optional per-shift breakdown. When present AND multiple shifts have
   * workloading data (or enough info to allocate proportionally), the
   * panel calls /sub-analysis/preview-multi and renders one accordion per
   * shift plus a lead-level rollup. Falls back to single-shift when not
   * enough per-shift data to disambiguate.
   */
  serviceTimes?: any[];
  strategicValue: StrategicValue;
  totalCost: number | undefined | null;
}

const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
const money = (n: number) =>
  `$${n.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })}`;

interface LineProps {
  emphasis?: boolean;
  label: string;
  value: string;
}
const LineDefaults = { emphasis: false };
const Line: React.FC<LineProps> = function Line({
  emphasis,
  label,
  value
}) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        my: 0.25
      }}
    >
      <Typography color="secondary.400" variant="body_100">
        {label}
      </Typography>
      <Typography
        fontWeight={emphasis ? 'bold' : 'normal'}
        variant="body_200"
      >
        {value}
      </Typography>
    </Box>
  );
};
Line.defaultProps = LineDefaults;

interface SignalBarProps {
  keepLabel?: string;
  keepPct?: number | null;
  label: string;
  partnerLabel?: string;
  partnerPct?: number | null;
  winner?: 'keep' | 'partner' | null;
}
const SignalBarDefaults = {
  keepLabel: undefined,
  keepPct: null,
  partnerLabel: undefined,
  partnerPct: null,
  winner: null
};
const SignalBar: React.FC<SignalBarProps> = function SignalBar({
  keepLabel,
  keepPct,
  label,
  partnerLabel,
  partnerPct,
  winner
}) {
  const renderBar = (
    side: 'keep' | 'partner',
    pct: number | null | undefined,
    text: string | undefined
  ) => {
    if (pct === null || pct === undefined) return null;
    const clamped = Math.max(0, Math.min(100, pct));
    const isWinner = winner === side;
    const fill = side === 'keep' ? '#1D7A3B' : '#1976D2';
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          mt: 0.25
        }}
      >
        <Typography
          color="secondary.400"
          sx={{ minWidth: 48 }}
          variant="body_100"
        >
          {side === 'keep' ? 'Keep' : 'Partner'}
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            flex: 1,
            height: 8,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              bgcolor: fill,
              height: '100%',
              opacity: isWinner ? 1 : 0.45,
              width: `${clamped}%`
            }}
          />
        </Box>
        <Typography
          sx={{
            fontWeight: isWinner ? 700 : 400,
            minWidth: 72,
            textAlign: 'right'
          }}
          variant="body_100"
        >
          {text ?? `${clamped.toFixed(2)}%`}
        </Typography>
      </Box>
    );
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        color="secondary.500"
        sx={{ fontWeight: 600 }}
        variant="body_100"
      >
        {label}
      </Typography>
      {renderBar('keep', keepPct, keepLabel)}
      {renderBar('partner', partnerPct, partnerLabel)}
    </Box>
  );
};
SignalBar.defaultProps = SignalBarDefaults;

interface ShiftBodyProps {
  heading?: string;
  result: SubAnalysisResult;
}
const ShiftBodyDefaults = { heading: undefined };
const ShiftBody: React.FC<ShiftBodyProps> = function ShiftBody({
  heading,
  result
}) {
  const best =
    result && result.bestOutsourceIndex >= 0
      ? result.outsource[result.bestOutsourceIndex]
      : null;
  return (
    <Box sx={{ mb: 3 }}>
      {heading && (
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            mb: 2,
            pb: 1
          }}
        >
          <Typography fontWeight="bold" variant="body_300">
            {heading}
          </Typography>
          <Box
            sx={{
              bgcolor:
                result.recommendation === 'outsource' ? '#E3F2FD' : '#E1F5E8',
              borderRadius: '12px',
              color:
                result.recommendation === 'outsource' ? '#1976D2' : '#1D7A3B',
              fontSize: '0.72rem',
              fontWeight: 700,
              px: 1,
              py: 0.25
            }}
          >
            {result.recommendation === 'outsource'
              ? `OUTSOURCE${best ? ` → ${best.partnerName}` : ''}`
              : 'KEEP IN-HOUSE'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { lg: '5fr 6fr', xs: '1fr' }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Keep card */}
          <Box
            sx={{
              border: '1px solid',
              borderColor:
                result.recommendation === 'keep'
                  ? 'success.light'
                  : 'divider',
              borderRadius: 1,
              p: 2
            }}
          >
            <Typography fontWeight="bold" variant="body_300">
              Keep (in-house)
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Line
              label="Your cost to deliver"
              value={money(result.keep.annualCost)}
            />
            <Line
              label="Client pays you"
              value={money(result.keep.annualPrice)}
            />
            <Divider sx={{ my: 0.75 }} />
            <Line
              emphasis
              label="Net to you (annual)"
              value={money(result.keep.annualPrice - result.keep.annualCost)}
            />
            <Line
              emphasis
              label="Margin"
              value={pct(result.keep.margin)}
            />
            <Line
              label="Your cost /hr"
              value={
                result.keep.capacityImpactHours > 0
                  ? `${money(
                      result.keep.annualCost /
                        result.keep.capacityImpactHours
                    )}/hr`
                  : '—'
              }
            />
            <Line
              label="Capacity impact"
              value={`+${result.keep.capacityImpactHours.toFixed(0)} hrs/yr`}
            />
            <Line
              label="Drive cost"
              value={money(result.keep.driveCostPerYear)}
            />
          </Box>
          {/* Partner card(s) */}
          {result.outsource.length === 0 && (
            <Box
              sx={{
                alignItems: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 2
              }}
            >
              <Typography color="secondary.400" variant="body_200">
                No eligible Partner Network candidates.
              </Typography>
              <Typography color="secondary.400" variant="body_100">
                Set `hourly_rate` on active partners to enable.
              </Typography>
            </Box>
          )}
          {result.outsource.map((partner, idx) => (
            <Box
              key={partner.partnerId}
              sx={{
                border: '1px solid',
                borderColor:
                  idx === result.bestOutsourceIndex &&
                  result.recommendation === 'outsource'
                    ? '#1976D2'
                    : 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography fontWeight="bold" variant="body_300">
                {partner.partnerName}
                {idx === result.bestOutsourceIndex && (
                  <Typography
                    color="primary.main"
                    component="span"
                    sx={{ ml: 1 }}
                    variant="body_100"
                  >
                    (best)
                  </Typography>
                )}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography
                color="secondary.500"
                sx={{ mt: 0.5 }}
                variant="body_100"
              >
                Cash in
              </Typography>
              <Line
                label="Client pays you"
                value={money(partner.annualPrice)}
              />
              <Line
                label="Partner pays you (overhead fee)"
                value={money(partner.overheadCost)}
              />
              <Typography
                color="secondary.500"
                sx={{ mt: 1 }}
                variant="body_100"
              >
                Cash out
              </Typography>
              <Line
                label="You pay partner"
                value={money(partner.partnerBilledCost)}
              />
              <Line
                label="Your QA / oversight time"
                value={money(partner.oversightCostPerYear)}
              />
              <Divider sx={{ my: 0.75 }} />
              <Line
                emphasis
                label="Net to you (annual)"
                value={money(partner.annualPrice - partner.annualCost)}
              />
              <Line emphasis label="Margin" value={pct(partner.margin)} />
              <Line
                label="Partner rate /hr"
                value={
                  result.keep.capacityImpactHours > 0
                    ? `${money(
                        partner.partnerBilledCost /
                          result.keep.capacityImpactHours
                      )}/hr`
                    : '—'
                }
              />
              <Line
                label="Capacity impact"
                value={`${partner.capacityImpactHours} hrs`}
              />
              <Line
                label="Partner fit"
                value={pct(partner.partnerFitScore)}
              />
              <Line
                label="Risk"
                value={`${pct(partner.riskScore)} (lower = safer)`}
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {best && (
            <Box
              sx={{
                bgcolor: 'common.white',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography
                color="secondary.400"
                sx={{ letterSpacing: 0.4, mb: 1.5, textTransform: 'uppercase' }}
                variant="body_100"
              >
                Decision Signals
              </Typography>
              {(() => {
                const keepMarginForCompare =
                  result.keep.adjustedMargin ?? result.keep.margin;
                const maxMargin = Math.max(
                  Math.abs(keepMarginForCompare),
                  Math.abs(best.margin),
                  0.01
                );
                const keepPct =
                  keepMarginForCompare >= 0
                    ? (keepMarginForCompare / maxMargin) * 100
                    : 0;
                const partnerPct =
                  best.margin >= 0 ? (best.margin / maxMargin) * 100 : 0;
                let saver: 'partner' | 'keep' | null = null;
                if (result.marginAdvantage > 0) saver = 'partner';
                else if (result.marginAdvantage < 0) saver = 'keep';
                const saveAmount = Math.abs(result.marginAdvantage);
                const adjustedActive =
                  result.keep.adjustedMargin !== undefined &&
                  result.keep.adjustedMargin !== result.keep.margin;
                return (
                  <Box>
                    <SignalBar
                      keepLabel={
                        adjustedActive
                          ? `${pct(keepMarginForCompare)} (raw ${pct(
                              result.keep.margin
                            )})`
                          : pct(result.keep.margin)
                      }
                      keepPct={keepPct}
                      label={
                        adjustedActive
                          ? 'Margin (capacity-adjusted)'
                          : 'Margin'
                      }
                      partnerLabel={pct(best.margin)}
                      partnerPct={partnerPct}
                      winner={
                        keepMarginForCompare >= best.margin
                          ? 'keep'
                          : 'partner'
                      }
                    />
                    {adjustedActive &&
                      result.keep.opportunityCostPerYear !== undefined && (
                        <Typography
                          color="secondary.400"
                          sx={{ display: 'block', mt: 0.25 }}
                          variant="body_100"
                        >
                          Opportunity cost of committed hours:{' '}
                          <strong>
                            {money(result.keep.opportunityCostPerYear)}/yr
                          </strong>
                        </Typography>
                      )}
                    {saver && saveAmount > 0 && (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 0.75,
                          mt: 0.5
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor:
                              saver === 'keep' ? '#E1F5E8' : '#E3F2FD',
                            borderRadius: '12px',
                            color: saver === 'keep' ? '#1D7A3B' : '#1976D2',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            px: 1,
                            py: 0.25
                          }}
                        >
                          {saver === 'keep' ? 'KEEP SAVES' : 'PARTNER SAVES'}
                        </Box>
                        <Typography color="secondary.500" variant="body_100">
                          {money(saveAmount)}/yr vs. the other option
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              {(() => {
                const hrs = result.keep.capacityImpactHours;
                return (
                  <SignalBar
                    keepLabel="0 hrs saved"
                    keepPct={0}
                    label="Capacity Freedom (hrs/yr freed)"
                    partnerLabel={`+${hrs.toFixed(0)} hrs saved`}
                    partnerPct={hrs > 0 ? 100 : 0}
                    winner={hrs > 0 ? 'partner' : 'keep'}
                  />
                );
              })()}
              {result.keep.opportunityCostPerYear !== undefined &&
                result.keep.annualPrice > 0 && (
                  <SignalBar
                    keepLabel={`${Math.max(
                      0,
                      100 -
                        (result.keep.opportunityCostPerYear /
                          result.keep.annualPrice) *
                          100
                    ).toFixed(0)}% retained`}
                    keepPct={Math.max(
                      0,
                      100 -
                        (result.keep.opportunityCostPerYear /
                          result.keep.annualPrice) *
                          100
                    )}
                    label="Flexibility Preserved"
                    partnerLabel="100% retained"
                    partnerPct={100}
                    winner="partner"
                  />
                )}
              <SignalBar
                label="Partner Fit"
                partnerLabel={pct(best.partnerFitScore)}
                partnerPct={best.partnerFitScore * 100}
              />
              <SignalBar
                label="Partner Risk (lower = safer)"
                partnerLabel={pct(best.riskScore)}
                partnerPct={best.riskScore * 100}
              />
            </Box>
          )}
          <Box
            sx={{
              bgcolor:
                result.recommendation === 'outsource' ? '#F3F8FD' : '#F1F9F3',
              border: '1px solid',
              borderColor:
                result.recommendation === 'outsource' ? '#BBDEFB' : '#C8E6C9',
              borderRadius: 1,
              p: 2
            }}
          >
            <Typography
              color={
                result.recommendation === 'outsource' ? '#1976D2' : '#1D7A3B'
              }
              sx={{ letterSpacing: 0.4, mb: 0.5, textTransform: 'uppercase' }}
              variant="body_100"
            >
              Why this recommendation
            </Typography>
            <Typography color="mainText.300" variant="body_200">
              {result.recommendationReason}
            </Typography>
            {result.marginAdvantage !== 0 && (
              <Typography
                color="secondary.400"
                sx={{ mt: 0.75 }}
                variant="body_100"
              >
                Cost advantage:{' '}
                <strong>{money(result.marginAdvantage)}/yr</strong>{' '}
                {result.marginAdvantage > 0
                  ? '(outsource saves)'
                  : '(keep saves)'}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
ShiftBody.defaultProps = ShiftBodyDefaults;

const hasAllRequired = (p: Props) =>
  p.annualPrice !== undefined &&
  p.annualPrice !== null &&
  p.annualPrice > 0 &&
  p.totalCost !== undefined &&
  p.totalCost !== null &&
  p.hoursPerYear !== undefined &&
  p.hoursPerYear !== null &&
  p.hoursPerYear > 0 &&
  p.monthlyValue !== undefined &&
  p.monthlyValue !== null;

/**
 * Calculator v3 §12.4 / §8 — Sub Analysis (Keep-vs-Outsource) panel.
 *
 * Posts the bid's current pricing + the client's strategic value to
 * /sub-analysis/preview and renders a side-by-side comparison: tenant's
 * keep scenario vs. every eligible Partner Network candidate. Ends in the
 * engine's recommendation with a human-readable rationale.
 *
 * Silently hides if the tenant doesn't have calculator_v3 enabled (403)
 * or if the bid is missing required pricing data.
 */
/** Build per-shift inputs from a lead's serviceTimes. Returns null if
 *  there isn't enough data to run multi-shift — caller falls back to
 *  single-shift with the lead-aggregate numbers. */
const buildShiftsFromServiceTimes = (
  serviceTimes: any[] | undefined,
  leadAnnualPrice: number,
  leadTotalCost: number,
  leadHours: number,
  leadDirectLabor: number
): ShiftAnalysisRequest[] | null => {
  if (!serviceTimes || serviceTimes.length < 2) return null;
  // First try: use each shift's workloadingOutputJson snapshot.
  const withSnapshots = serviceTimes.filter(
    (st) => st?.workloadingOutputJson?.hoursPerYear
  );
  if (withSnapshots.length >= 2) {
    return withSnapshots.map((st, i) => {
      const snap = st.workloadingOutputJson || {};
      const hrs = Number(snap.hoursPerYear ?? 0);
      const price = Number(snap.annualPrice ?? 0);
      const cost = Number(snap.cost?.totalCost ?? 0);
      const direct = Number(snap.cost?.directLaborCost ?? 0);
      const monthly = price > 0 ? price / 12 : 0;
      return {
        keep: {
          annualPrice: price,
          directLaborCost: direct,
          hoursPerYear: hrs,
          monthlyValue: monthly,
          totalCost: cost
        },
        shiftId: String(st?.id ?? i),
        shiftName: st?.shiftName || st?.name || `Shift ${i + 1}`
      };
    });
  }
  // Second try: allocate lead-aggregate by each shift's hours/yr.
  const shiftHours = serviceTimes.map((st) => {
    const weekly = Number(
      st?.availability?.generalInfo?.serviceTimeHoursPerWeek ??
        st?.serviceTimeHoursPerWeek ??
        0
    );
    const weeks = Number(
      st?.availability?.generalInfo?.operatingWeeks ??
        st?.operatingWeeks ??
        52
    );
    return weekly * weeks;
  });
  const totalShiftHours = shiftHours.reduce((a, b) => a + b, 0);
  if (totalShiftHours <= 0 || leadHours <= 0) return null;
  return serviceTimes.map((st, i) => {
    const share = shiftHours[i] / totalShiftHours;
    const allocatedHours = leadHours * share;
    const allocatedPrice = leadAnnualPrice * share;
    const allocatedCost = leadTotalCost * share;
    const allocatedDirect = leadDirectLabor * share;
    return {
      keep: {
        annualPrice: allocatedPrice,
        directLaborCost: allocatedDirect,
        hoursPerYear: allocatedHours,
        monthlyValue: allocatedPrice > 0 ? allocatedPrice / 12 : 0,
        totalCost: allocatedCost
      },
      shiftId: String(st?.id ?? i),
      shiftName: st?.shiftName || st?.name || `Shift ${i + 1}`
    };
  });
};

const SubAnalysisPanelDefaults = {
  contractDurationMonths: undefined,
  serviceTimes: undefined
};
const SubAnalysisPanel: React.FC<Props> = function SubAnalysisPanel({
  annualPrice,
  contractDurationMonths,
  directLaborCost,
  hoursPerYear,
  monthlyValue,
  serviceTimes,
  strategicValue,
  totalCost
}) {
  const [result, setResult] = useState<SubAnalysisResult | null>(null);
  const [multiResult, setMultiResult] =
    useState<SubAnalysisMultiResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hidden, setHidden] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const propsForCheck: Props = {
    annualPrice,
    directLaborCost,
    hoursPerYear,
    monthlyValue,
    strategicValue,
    totalCost
  };

  const run = async () => {
    if (!hasAllRequired(propsForCheck)) return;
    setLoading(true);
    setError(null);
    try {
      const shifts = buildShiftsFromServiceTimes(
        serviceTimes,
        Number(annualPrice ?? 0),
        Number(totalCost ?? 0),
        Number(hoursPerYear ?? 0),
        Number(directLaborCost ?? 0)
      );
      if (shifts && shifts.length >= 2) {
        const res = await api.SubAnalysisApi.previewMulti({
          contractDurationMonths:
            contractDurationMonths != null && contractDurationMonths > 0
              ? Number(contractDurationMonths)
              : undefined,
          shifts,
          strategicValue
        });
        setMultiResult(res);
        setResult(null);
      } else {
        const res = await api.SubAnalysisApi.preview({
          contractDurationMonths:
            contractDurationMonths != null && contractDurationMonths > 0
              ? Number(contractDurationMonths)
              : undefined,
          keep: {
            annualPrice: Number(annualPrice),
            directLaborCost: Number(directLaborCost ?? 0),
            hoursPerYear: Number(hoursPerYear),
            monthlyValue: Number(monthlyValue),
            totalCost: Number(totalCost)
          },
          strategicValue
        });
        setResult(res);
        setMultiResult(null);
      }
      setHidden(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHidden(true);
      } else {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            'Sub analysis failed.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
  }, [
    annualPrice,
    contractDurationMonths,
    hoursPerYear,
    monthlyValue,
    strategicValue,
    totalCost,
    // Re-run when the number of shifts changes or their snapshots update.
    JSON.stringify(
      (serviceTimes ?? []).map((st: any) => ({
        id: st?.id,
        snap: st?.workloadingOutputJson?.hoursPerYear,
        wk: st?.availability?.generalInfo?.serviceTimeHoursPerWeek
      }))
    )
  ]);

  if (hidden) return null;

  // Show a placeholder when inputs are incomplete so operators know the
  // panel exists and what's blocking it — don't silently hide.
  if (!hasAllRequired(propsForCheck)) {
    return (
      <Accordion
        defaultExpanded
        sx={{
          '&:before': { display: 'none' },
          bgcolor: 'common.white',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: '8px !important',
          boxShadow: 'none',
          mt: 3
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography fontWeight="bold" variant="h6">
              Keep vs Outsource Analysis
            </Typography>
            <Typography color="secondary.400" variant="body_100">
              Inputs incomplete — panel will activate when ready.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography color="secondary.400" variant="body_200">
            Will activate once the bid has pricing + hours:
          </Typography>
          <Box component="ul" sx={{ color: 'secondary.400', mt: 1 }}>
            {(!annualPrice || annualPrice <= 0) && (
              <li>annual price (generate a shift with hours on the grid)</li>
            )}
            {(!hoursPerYear || hoursPerYear <= 0) && (
              <li>total hours per year (sum of service-time hours)</li>
            )}
            {!monthlyValue && <li>monthly contract value</li>}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  const best =
    result && result.bestOutsourceIndex >= 0
      ? result.outsource[result.bestOutsourceIndex]
      : null;

  return (
    <Accordion
      defaultExpanded
      sx={{
        '&:before': { display: 'none' },
        bgcolor: 'common.white',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
        mt: 3
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            mr: 2
          }
        }}
      >
        <Box>
          <Typography fontWeight="bold" variant="h6">
            Keep vs Outsource Analysis
          </Typography>
          <Typography color="secondary.400" variant="body_100">
            {(() => {
              if (multiResult) {
                return `${multiResult.shifts.length} shifts — ${multiResult.rollup.shiftsRecommendedKeep} keep, ${multiResult.rollup.shiftsRecommendedOutsource} outsource`;
              }
              if (result) {
                const partnerSuffix =
                  result.recommendation === 'outsource' && best
                    ? ` (${best.partnerName})`
                    : '';
                return `Recommend: ${result.recommendation}${partnerSuffix}`;
              }
              return 'Compares keep scenario to eligible Partner Network candidates.';
            })()}
          </Typography>
        </Box>
        <Button
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            run();
          }}
          onFocus={(e) => e.stopPropagation()}
          size="small"
          variant="outlined"
        >
          {loading ? 'Computing…' : 'Refresh'}
        </Button>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, py: 0 }}>
      {error && (
        <Typography color="error.main" variant="body_100">
          {error}
        </Typography>
      )}

      {!result && loading && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body_200">
            Loading keep-vs-outsource analysis…
          </Typography>
        </Box>
      )}

      {result && !multiResult && <ShiftBody result={result} />}
      {multiResult && (
        <Box>
          {/* Lead-level rollup */}
          <Box
            sx={{
              bgcolor: '#F1F9F3',
              border: '1px solid #C8E6C9',
              borderRadius: 1,
              mb: 2,
              p: 2
            }}
          >
            <Typography
              color="secondary.400"
              sx={{
                letterSpacing: 0.4,
                mb: 1,
                textTransform: 'uppercase'
              }}
              variant="body_100"
            >
              Lead-level Rollup
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  md: 'repeat(4, 1fr)',
                  xs: 'repeat(2, 1fr)'
                }
              }}
            >
              <Box>
                <Typography color="secondary.400" variant="body_100">
                  If we follow every recommendation
                </Typography>
                <Typography fontWeight="bold" variant="body_300">
                  Net to you {money(multiResult.rollup.netToTenantIfRecommended)}
                </Typography>
              </Box>
              <Box>
                <Typography color="secondary.400" variant="body_100">
                  Savings vs all-keep
                </Typography>
                <Typography fontWeight="bold" variant="body_300">
                  {money(multiResult.rollup.savingsVsAllKeep)}
                </Typography>
              </Box>
              <Box>
                <Typography color="secondary.400" variant="body_100">
                  Total client price
                </Typography>
                <Typography variant="body_300">
                  {money(multiResult.rollup.totalAnnualPrice)}
                </Typography>
              </Box>
              <Box>
                <Typography color="secondary.400" variant="body_100">
                  Shifts recommend
                </Typography>
                <Typography variant="body_300">
                  {multiResult.rollup.shiftsRecommendedKeep} keep ·{' '}
                  {multiResult.rollup.shiftsRecommendedOutsource} outsource
                </Typography>
              </Box>
            </Box>
          </Box>
          {multiResult.shifts.map((s) => (
            <ShiftBody
              heading={s.shiftName || `Shift ${s.shiftId}`}
              key={String(s.shiftId)}
              result={s.analysis}
            />
          ))}
        </Box>
      )}
      </AccordionDetails>
    </Accordion>
  );
};
SubAnalysisPanel.defaultProps = SubAnalysisPanelDefaults;

export default SubAnalysisPanel;
